import styled from '@emotion/styled';
import { useCallback, useState } from 'react';
import { Spacings } from '@styles/index';
import useUpscCurrentAffairsFaqItems, {
  UpscFaqSessionKey
} from '@api/hooks/upscCurrentAffairs/useUpscCurrentAffairsFaqItems';

const FaqSection = styled.section<{ $embedded?: boolean }>`
  max-width: ${({ $embedded }) => ($embedded ? 'none' : '1136px')};
  margin: ${({ $embedded }) => ($embedded ? '16px 0 0' : '0 auto')};
  padding: ${({ $embedded }) => ($embedded ? '0 0 16px' : '0 16px 40px')};
`;

const FaqBlock = styled.div`
  padding: 20px;
  border: 1px solid var(--color-divider);
  border-radius: ${Spacings.SPACING_4B};
  background: var(--color-base-1);
  box-shadow: 0 2px 12px rgba(60, 72, 82, 0.08);
`;

const FaqHeading = styled.h2`
  margin: 0 0 16px;
  font-size: 20px;
  font-weight: 700;
  color: var(--color-accent-blue);
`;

const FaqItem = styled.div`
  padding: 11px 0;
  border-bottom: 1px solid var(--color-divider-secondary);

  &:last-of-type {
    border-bottom: none;
  }
`;

const FaqQuestionButton = styled.button`
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
  text-align: left;
  color: var(--color-text-primary);
  cursor: pointer;
`;

const FaqIcon = styled.span<{ $isOpen: boolean }>`
  flex-shrink: 0;
  margin-top: -1px;
  font-size: 18px;
  line-height: 1;
  color: var(--color-accent-blue);
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(45deg)' : 'none')};
`;

const FaqAnswer = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  margin-top: 9px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.75;
  color: var(--color-text-primary);
  opacity: 0.85;
`;

export interface UpscCurrentAffairsFaqProps {
  faqSessionKey: UpscFaqSessionKey;
  allowMultipleOpen?: boolean;
  embedded?: boolean;
}

const UpscCurrentAffairsFaq = ({
  faqSessionKey,
  allowMultipleOpen = true,
  embedded = false
}: UpscCurrentAffairsFaqProps) => {
  const { faqItems, isLoading } = useUpscCurrentAffairsFaqItems();
  const data = faqItems[faqSessionKey];
  const [openIndices, setOpenIndices] = useState<Set<number>>(() => new Set());

  const toggleItem = useCallback(
    (index: number) => {
      setOpenIndices((prev) => {
        const next = new Set(prev);
        if (next.has(index)) {
          next.delete(index);
          return next;
        }
        if (!allowMultipleOpen) {
          return new Set([index]);
        }
        next.add(index);
        return next;
      });
    },
    [allowMultipleOpen]
  );

  if (isLoading || !data?.faqSession?.length) {
    return null;
  }

  return (
    <FaqSection $embedded={embedded} aria-label="Frequently Asked Questions">
      <FaqBlock>
        <FaqHeading>{data.faqHeading}</FaqHeading>
        {data.faqSession.map((item, index) => {
          const isOpen = openIndices.has(index);

          return (
            <FaqItem key={item.id ?? `faq-${index}`}>
              <FaqQuestionButton
                type="button"
                aria-expanded={isOpen}
                onClick={() => toggleItem(index)}
              >
                <span>{item.faqQuestion}</span>
                <FaqIcon $isOpen={isOpen} aria-hidden>
                  +
                </FaqIcon>
              </FaqQuestionButton>
              <FaqAnswer $isOpen={isOpen}>{item.faqAnswer}</FaqAnswer>
            </FaqItem>
          );
        })}
      </FaqBlock>
    </FaqSection>
  );
};

export default UpscCurrentAffairsFaq;
